import { CLASSIFICATIONS_TO_EXCLUDE } from '@dupe/common/constants'
import logger from '~/src/lib/logger'

type NewJsonFormat = {
  results: {
    bboxes: {
      x: number
      y: number
      w: number
      h: number
      label: string
    }[]
  }
  image: {
    url: string
    width: number
    height: number
  }
}

type ExistingFormat = {
  error: {
    details: {
      hasError: boolean
      imageUrl: string
      usingProxy: boolean
    }[]
  }
  context: null
  webDetection: null
  faceAnnotations: []
  logoAnnotations: []
  textAnnotations: []
  labelAnnotations: []
  fullTextAnnotation: null
  cropHintsAnnotation: null
  landmarkAnnotations: []
  productSearchResults: null
  safeSearchAnnotation: null
  imagePropertiesAnnotation: null
  localizedObjectAnnotations: {
    mid: string
    name: string
    score: number
    boundingPoly: {
      vertices: []
      normalizedVertices: { x: number; y: number }[]
    }
    languageCode: string
  }[]
}[]

export function convertNewJsonToExistingFormat(
  newJson: NewJsonFormat,
  originalWidth: number,
  originalHeight: number
): ExistingFormat {
  let results = newJson?.results || { bboxes: [] }
  const image = newJson?.image || { url: '', width: 0, height: 0 }
  const url = image?.url || ''

  // if originalWidth or originalHeight is not a number, return an empty array
  if (typeof originalWidth !== 'number' || typeof originalHeight !== 'number') {
    results = { bboxes: [] }
  }

  return [
    {
      error: {
        details: [{ hasError: false, imageUrl: url, usingProxy: true }],
      },
      context: null,
      webDetection: null,
      faceAnnotations: [],
      logoAnnotations: [],
      textAnnotations: [],
      labelAnnotations: [],
      fullTextAnnotation: null,
      cropHintsAnnotation: null,
      landmarkAnnotations: [],
      productSearchResults: null,
      safeSearchAnnotation: null,
      imagePropertiesAnnotation: null,
      localizedObjectAnnotations: results.bboxes.map((box) => ({
        mid: '',
        name: box.label,
        score: 1.0, // Placeholder, no score provided in new JSON format
        boundingPoly: {
          vertices: [],
          normalizedVertices: [
            { x: box.x / originalWidth, y: box.y / originalHeight },
            { x: (box.x + box.w) / originalWidth, y: box.y / originalHeight },
            {
              x: (box.x + box.w) / originalWidth,
              y: (box.y + box.h) / originalHeight,
            },
            { x: box.x / originalWidth, y: (box.y + box.h) / originalHeight },
          ],
        },
        languageCode: '',
      })),
    },
  ]
}

// remove overlapping objects
type Vertex = { x: number; y: number }
type BoundingPoly = { normalizedVertices: Vertex[] }
type ObjectData = {
  score: number
  totalArea: number
  name: string
  boundingPoly: BoundingPoly
}

interface BoundingBox {
  x1: number
  y1: number
  x2: number
  y2: number
}

function getBoundingBox(vertices: { x: number; y: number }[]): BoundingBox {
  const xs = vertices.map((v) => v.x)
  const ys = vertices.map((v) => v.y)
  return {
    x1: Math.min(...xs),
    y1: Math.min(...ys),
    x2: Math.max(...xs),
    y2: Math.max(...ys),
  }
}

function doBoxesOverlap(
  box1: BoundingBox,
  box2: BoundingBox,
  overlapThreshold = 0.8
): { overlaps: boolean; overlapRatio: number } {
  // Calculate intersection
  const xOverlap = Math.max(
    0,
    Math.min(box1.x2, box2.x2) - Math.max(box1.x1, box2.x1)
  )
  const yOverlap = Math.max(
    0,
    Math.min(box1.y2, box2.y2) - Math.max(box1.y1, box2.y1)
  )
  const intersectionArea = xOverlap * yOverlap

  // Calculate areas
  const box1Area = (box1.x2 - box1.x1) * (box1.y2 - box1.y1)
  const box2Area = (box2.x2 - box2.x1) * (box2.y2 - box2.y1)

  // Calculate overlap ratio relative to the smaller box's area
  const smallerArea = Math.min(box1Area, box2Area)
  const overlapRatio = intersectionArea / smallerArea

  // If more than 80% of the smaller object is overlapped, consider it as overlapping
  return { overlaps: overlapRatio > overlapThreshold, overlapRatio }
}

export function removeOverlappingObjects(objects: ObjectData[]): ObjectData[] {
  //first remove CLASSIFICATIONS_TO_EXCLUDE
  objects = objects.filter(
    (obj) => !CLASSIFICATIONS_TO_EXCLUDE.includes(obj.name.toLowerCase())
  )

  // Sort by area first, then by score as a tiebreaker
  let sortedObjects = [...objects].sort((a, b) => {
    if (a.totalArea !== b.totalArea) {
      return b.totalArea - a.totalArea // Larger areas first
    }
    return b.score - a.score // If same area, higher score wins
  })

  let keptObjects: ObjectData[] = []

  for (let objA of sortedObjects) {
    const boxA = getBoundingBox(objA.boundingPoly.normalizedVertices)
    const areaA = (boxA.x2 - boxA.x1) * (boxA.y2 - boxA.y1)

    let shouldKeep = true

    // Check if this object overlaps with any already kept object
    for (let objB of keptObjects) {
      const boxB = getBoundingBox(objB.boundingPoly.normalizedVertices)
      const { overlaps, overlapRatio } = doBoxesOverlap(boxA, boxB)

      if (overlaps) {
        // Since we sorted by area, if there's a significant overlap (>80% of smaller object),
        // this object must be smaller and mostly contained within the larger one
        shouldKeep = false
        logger.debug('Removing smaller overlapping object:', {
          removedObject: objA.name,
          removedArea: areaA,
          keptObject: objB.name,
          keptArea: objB.totalArea,
          overlapRatio: overlapRatio,
          reason: 'More than 80% covered by larger object',
        })
        break
      }
    }

    if (shouldKeep) {
      keptObjects.push({ ...objA, name: objA.name.toLowerCase() })
    }
  }

  return keptObjects
}
